<template>
  <div class="w-full flex flex-col gap-4 font-rubik find-inventory-main-container">
    <h2 class="find-inventory-h2">Get in touch</h2>
    <div>
      <h3 class="find-inventory-h3">Find an Inventory</h3>

      <div class="mt-3 find-inventory-outer-container">
        <div class="flex flex-row justify-between items-center gap-1">
          <div class="input-container flex flex-row justify-around items-center">
            <div :class="['flex flex-row justify-center items-center gap-1 geolocate-container', { 'geolocate-container-loading' : loading }]">
              <geolocate @success="searchByCoordinates" :showIcon="false" :onlyIcon="true" :iconHeight="'h-5'" class="mb-2" />
              <div class="hidden 1170-bp:inline"><span class="input-location-text">Use current location</span></div>
              <div class="hidden 1170-bp:inline" v-if="loading"><img class="animate-spin h-4" src="@/images/spinner.svg" alt="Loading"></div>
            </div>
            <input
              @keyup.enter="search"
              v-model="query"
              @click="coordinates = null"
              :class="{ 'is-danger': error }"
              class="find-inventory-input bg-transparent w-full focus:ring-0 focus:outline-none"
              type="search"
              placeholder="Where are you located?"
            />
            <img @click="search" class="w-4" src="@/images/header/search-II.png" alt="search" />
          </div>
        </div>
        <div class="search-results-container">
          <div v-if="error" class="help py-1 text-center bg-white text-red-500 border rounded-md">
            {{ error }}
            <b><a @click.prevent="resetSearch()">Try Again</a></b>
          </div>
          <div v-if="cities.length" role="menu" class="mt-1 border rounded-md">
            <div class="dropdown-content modal-card-body" style="max-height: 300px !important;">
              <p class="dropdown-item"><b>We found mulitple results...</b></p>
              <a v-for="city in cities" :key="city.id" :href="cityPath(city.node)" class="dropdown-item">
                {{ city.node.name }}, {{ city.node.stateId | upcase }}
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>    
  </div>
</template>

<script>
import ahoy from 'shared/ahoy'
import Geolocate from '../../location-search/geolocate.vue'
import { zipQuery, citySearchQuery, geoSearchQuery, solutionByCity } from 'shared/graphql/queries'
import { graphqlQuery } from 'shared/graphql'
import { cityPath } from '../../../shared/city-helper'
import { mapState } from 'vuex'
const ZIP_REGEX = /^\d{5}$/
const redirectToCity = (city) => window.location.href = cityPath(city)

export default {
  components: {
    Geolocate
  },
  data() {
    return {
      currentImageIndex: 0,
      imageOpacity: 1,
      
      loading: 0,
      solutionSlug: '',
      error: null,
      query: '',
      coordinates: null,
      cities: []
    }
  },

  filters: {
    upcase(string) {
      return string.toUpperCase()
    }
  },
  computed: {
    ...mapState('shared', ['zipcode']),
    ...mapState('quote', ['lead']),
    isButtonDisabled() {
      return !/^(\d{5})|(\D{3,})$/i.test(this.query)
    }
  },
  watch: {
    query() {
      if (this.query === '') {
        this.error = null
        this.cities = []
      } else if (this.error && this.query !== this.lastQuery) {
        this.error = null
      }
    }
  },
  mounted() {    
    this.loading = 0
    this.query = this.zipcode
  },
  methods: {
    cityPath,
    handleClose() {
      this.$el.parentNode.removeChild(this.$el)
      this.$emit('close')
    },
    searchByCoordinates(coordinates) {
      this.coordinates = coordinates
      this.query = ''
      this.search()
    },
    resetSearch() {
      this.query = ''
      this.error = null
    },
    getRequestBody() {
      if (ZIP_REGEX.test(this.query)) {
        return { query: zipQuery, variables: { id: this.query } }
      }
      if (this.coordinates) {
        return { query: geoSearchQuery, variables: { coordinates: this.coordinates } }
      }
      const cityOnly = this.query.replace(/[, ]+[a-z]{2}\s*$/i, '').trim()
      return { query: citySearchQuery, variables: { query: cityOnly } }
    },
    async trackSearch() {
      ahoy.track('location.search', {
        coordinates: this.coordinates,
        query: this.query
      })
    },
    async search() {
      this.cities = []
      this.loading++
      this.lastQuery = this.query = this.query.trim()
      try {
        this.trackSearch()
        const result = await graphqlQuery(this.getRequestBody())
        if (result.data.zipcode) {
          const aboutSlug = `/service-areas/${result.data.zipcode.city.id}/`
          localStorage.setItem('serviceAreaAboutSlug', JSON.stringify(aboutSlug)) 
        } 
        return this.handleResult(result.data || {})
      } catch (e) {
        console.warn(e)
        this.error = 'There was an error while searching.'
        this.loading = 0
      }
    },
    async handleResult(data) {
      let { zipcode, zipcodes, cities } = data
      if (!zipcode && zipcodes?.edges?.length === 1) zipcode = zipcodes.edges[0].node

      if (this.solutionSlug && zipcode) {
        const sol = await this.getSolutionPage(zipcode)
        if (sol?.solutionByCity?.solution) {
          if (this.lead) {
            await this.$store.dispatch('quote/lead.zipcode.update', { lead: this.lead, zipcode: zipcode.id })
          } else {
            this.$store.commit('shared/zipcode.set', zipcode.id)
          }
          this.$emit('close')
          this.loading = 0
          window.location.href = `/partners/${sol.solutionByCity.organization.id}/${sol.solutionByCity.solution.slug}`
          return
        }
      }

      if (zipcode) {
        if (this.lead) {
          await this.$store.dispatch('quote/lead.zipcode.update', { lead: this.lead, zipcode: zipcode.id })
        } else {
          this.$store.commit('shared/zipcode.set', zipcode.id)
        }
        this.$emit('close')
        return redirectToCity(zipcode.city)
      }

      if (cities?.edges?.length) {
        const stateToken = (this.query.match(/([a-z]{2})\s*$/i) || [])[1]?.toLowerCase() || null
        const baseName   = this.query.replace(/[, ]+[a-z]{2}\s*$/i, '').trim().toLowerCase()

        const ranked = cities.edges.map(edge => {
          const n = edge.node
          const exact   = n.name.toLowerCase() === baseName
          const inState = stateToken && n.stateId.toLowerCase() === stateToken
          let rank = 3
          if (exact)        rank = inState ? 0 : 1
          else if (inState) rank = 2
          return { rank, edge }
        })

        this.cities  = ranked.sort((a, b) => a.rank - b.rank).map(r => r.edge)
        this.loading = 0

        if (this.cities.length === 1 && ranked[0].rank === 0) {
          const n = this.cities[0].node
          this.$emit('close')
          return redirectToCity(n)
        }
        return
      }

      this.error  = 'Sorry, we couldn’t find that location.'
      this.loading = 0
    },
    async getSolutionPage(zipcode) {
      const body = {
        query: solutionByCity,
        variables: {
          cityId: zipcode.city.id,
          solutionSlug: this.solutionSlug
        }
      }
      const result = await graphqlQuery(body)
      return result.data
    }
  }
}
</script>

<style scoped>
  .find-inventory-outer-container {
    position: relative;
  }

  .search-results-container {
    position: absolute;
    top: 100%;
    left: 0;
    width: 560px;
    z-index: 10000;
  }

  .find-inventory-h2 {
    font-size: 36px;
    font-weight: 400;
    line-height: 57.6px;
    letter-spacing: -0.04em;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #334155;
  }

  .find-inventory-h3 {
    font-size: 24px;
    font-weight: 400;
    line-height: 33.6px;
    letter-spacing: -0.04em;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #334155;
  }

  .input-container {
    width: 560px;
    height: 40px;
    padding: 18px 10px 18px 0px;
    gap: 10px;
    border-radius: 8px;
    border: 1px solid #EDE9E3;
    background-color: #FFFFFF;
    position: relative;
    z-index: 1; 
  }

  .geolocate-container {
    background-color: #F9F8F6;
    min-width: 150px;
    max-height: 36px;
    padding-left: 5px;
    padding-right: 5px;
    border-right: 1px solid #EDE9E3;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
 
  .geolocate-container-loading {
    min-width: 180px;
  }

  .input-location-text {
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: -0.0125em;
    color: #334155;
  }

  .find-inventory-input {
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: -0.0125em;
    color: #334155;
  }

  /* Mobile Styles */
  @media (max-width: 1170px) {
    .find-inventory-main-container {
      padding: 0px 20px 20px 20px
    }
    .geolocate-container {
      min-width: 44px;
    }
    .search-results-container {
      width: 370px;
    }
    .input-container,
    .search-results-container {
      width: 100%;
    }
  }
</style>